import { useMemo } from 'react'

import { useStore } from '@xyflow/react'

import NodeType from 'types/transformation/enums/NodeType'
import { InputNode, Node as TNode } from 'types/transformation/types/nodes'
import { useSourceSchemas } from 'redux/hooks/SimTables'

import { NODE_CATALOG } from '../catalog'

/** Node title per type, built from the palette catalog (e.g. "Aggregate"). */
const NODE_TITLES: Partial<Record<NodeType, string>> = (() => {
  const titles: Partial<Record<NodeType, string>> = {}

  for (const group of NODE_CATALOG) {
    for (const item of group.items) {
      titles[item.type] = item.title
    }
  }

  return titles
})()

/**
 * Maps each node id to an origin label for the columns it outputs, for the
 * board's field selectors:
 *   - an Input node → its source table name (e.g. "Ärenden"), so source
 *     columns show where the data actually comes from;
 *   - any other node → that node's title (e.g. "Aggregate", "Calculate"), so a
 *     column created mid-pipeline still shows a meaningful origin instead of a
 *     lone node number.
 *
 * Subscribes to node id/type/source only, so it stays stable while nodes are
 * dragged around the board.
 */
export function useColumnOrigins(): Map<string, string> {
  const [sourceTableMap] = useSourceSchemas()

  const nodeInfos = useStore(
    (store) =>
      store.nodes.map(
        (node) =>
          [
            node.id,
            (node.data as TNode['data']).type,
            (node.data as InputNode).source_table_id ?? null
          ] as [string, NodeType, string | null]
      ),
    (a, b) =>
      a.length === b.length &&
      a.every(
        (info, index) =>
          info[0] === b[index][0] &&
          info[1] === b[index][1] &&
          info[2] === b[index][2]
      )
  )

  return useMemo(() => {
    const origins = new Map<string, string>()

    for (const [nodeId, type, sourceTableId] of nodeInfos) {
      if (type === NodeType.INPUT) {
        const table = sourceTableId
          ? sourceTableMap.get(sourceTableId)
          : undefined

        if (table) {
          origins.set(nodeId, table.label)
        }
      } else {
        const title = NODE_TITLES[type]

        if (title) {
          origins.set(nodeId, title)
        }
      }
    }

    return origins
  }, [nodeInfos, sourceTableMap])
}
